import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
    FaBrain, FaNetworkWired, FaCodeBranch, FaProjectDiagram, FaServer, FaCubes,
    FaCheckCircle, FaChevronDown, FaChevronUp, FaChevronRight, FaPython, FaDatabase,
    FaChartArea, FaCloudUploadAlt
} from 'react-icons/fa';
import heroImage from '../../assets/download (2).png';

const MachineLearningPage = () => {
    const [openFaq, setOpenFaq] = useState(null);

    const toggleFaq = (index) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    const services = [
        { icon: FaBrain, title: 'Predictive Analytics', desc: 'Forecast sales, demand, and market trends with high precision.', image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600' },
        { icon: FaNetworkWired, title: 'Deep Learning', desc: 'Neural networks for complex pattern recognition in image and voice.', image: 'https://images.unsplash.com/photo-1516110833967-0b5716ca1387?w=600' },
        { icon: FaProjectDiagram, title: 'Recommender Systems', desc: 'Personalized product suggestions to boost user engagement.', image: 'https://images.unsplash.com/photo-1518186285589-2f7649de83e0?w=600' },
        { icon: FaCubes, title: 'Anomaly Detection', desc: 'Identify fraud and outliers in real-time transaction data.', image: 'https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=600' },
        { icon: FaServer, title: 'MLOps', desc: 'Scalable deployment pipelines to keep your models in production.', image: 'https://images.unsplash.com/photo-1558494949-ef526b01201b?w=600' },
        { icon: FaCodeBranch, title: 'Data Engineering', desc: 'Building the robust pipelines that feed your AI models.', image: 'https://images.unsplash.com/photo-1555421689-d68471e189f2?w=600' }
    ];

    const techStack = [
        { name: 'Python', icon: FaPython },
        { name: 'TensorFlow', icon: FaBrain },
        { name: 'PyTorch', icon: FaNetworkWired },
        { name: 'Scikit-Learn', icon: FaCodeBranch },
        { name: 'Pandas', icon: FaDatabase },
        { name: 'AWS SageMaker', icon: FaCloudUploadAlt }
    ];

    // Horizontal Flow Steps (ML Lifecycle)
    const flowSteps = [
        { title: 'Define', icon: FaChartArea, color: 'from-purple-500 to-indigo-600' },
        { title: 'Collect', icon: FaDatabase, color: 'from-blue-500 to-cyan-500' },
        { title: 'Train', icon: FaBrain, color: 'from-pink-500 to-rose-500' },
        { title: 'Evaluate', icon: FaCheckCircle, color: 'from-green-500 to-emerald-500' },
        { title: 'Deploy', icon: FaCloudUploadAlt, color: 'from-orange-500 to-red-500' },
        { title: 'Monitor', icon: FaServer, color: 'from-teal-400 to-blue-600' }
    ];

    const faqs = [
        { q: 'What problems can Machine Learning solve?', a: 'Everything from predicting customer churn and optimizing supply chains to automating quality control and personalizing marketing.' },
        { q: 'Do you offer custom model development?', a: 'Yes. We don\'t just use off-the-shelf APIs. We build and train custom models on your specific data for maximum accuracy.' },
        { q: 'What is MLOps?', a: 'It\'s DevOps for Machine Learning. It ensures your models are versioned, tested, and deployed reliably, preventing "model drift" over time.' },
        { q: 'How do you handle data privacy?', a: 'We can strip PII (Personal Identifiable Information) before training, and we can deploy models on your own secure infrastructure.' },
        { q: 'Can you improve our existing models?', a: 'Yes, we audit existing architectures, optimize hyperparameters, and retrain on cleaner data to boost performance.' }
    ];

    const whyChooseUs = [
        { title: 'State-of-the-Art Models', desc: 'We stay current with the latest research in Transformers, GANs, and RL.' },
        { title: 'Production-Ready Code', desc: 'We write clean, modular code that integrates seamlessly into your production stack.' },
        { title: 'Scalable Architecture', desc: 'Our solutions handle millions of predictions per second.' },
        { title: 'Transparent Results', desc: 'We provide clear metrics (AUC, F1-Score) so you know exactly how well the model works.' },
        { title: 'Cross-Industry Experience', desc: 'From FinTech to Healthcare, we\'ve seen your data challenges before.' },
        { title: 'Rapid Prototyping', desc: 'We validate ideas quickly with MVPs before committing to full-scale training.' }
    ];

    return (
        <div className="bg-white font-sans">
            {/* Hero Section */}
            <section className="relative h-[60vh] min-h-[500px] overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url('${heroImage}')` }}
                >
                    <div className="absolute inset-0 bg-gradient-to-r from-indigo-900/95 via-purple-900/90 to-transparent"></div>
                </div>

                <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-12 flex items-center pt-20">
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        className="max-w-3xl text-white"
                    >
                        <p className="text-xs font-light tracking-widest uppercase text-purple-300 mb-4">Innovation &gt; Data Science</p>
                        <h1 className="text-5xl md:text-6xl font-light mb-6 leading-tight">Machine <br /><span className="text-purple-400 font-normal">Learning</span></h1>
                        <p className="text-lg font-light text-purple-100 leading-relaxed mb-8 max-w-2xl">
                            Turn raw data into actionable foresight. We build intelligent systems that learn, adapt, and improve.
                        </p>
                        <div className="flex flex-wrap gap-4">
                            <a href="#inquiry" className="px-8 py-3 bg-purple-600 text-white font-medium rounded-sm hover:bg-purple-700 hover:shadow-lg transition-all text-sm uppercase tracking-wide">
                                Discuss Your Data
                            </a>
                            <a href="#services" className="px-8 py-3 border border-purple-300 text-white font-medium rounded-sm hover:bg-white hover:text-purple-900 transition-all text-sm uppercase tracking-wide">
                                Our Models
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Intro Stats */}
            <section className="py-24 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="max-w-4xl mx-auto text-center mb-20">
                        <h2 className="text-3xl md:text-4xl font-light text-slate-900 mb-6">Intelligence at Scale</h2>
                        <p className="text-lg text-slate-600 leading-relaxed">
                            Processing complexity to deliver simplicity.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-4 gap-12 text-center">
                        {[
                            { label: 'Predictions / Sec', val: '10k+' },
                            { label: 'Model Accuracy', val: '99.5%' },
                            { label: 'Data Points', val: '1Bn+' },
                            { label: 'ROI Improvement', val: '3x' }
                        ].map((stat, idx) => (
                            <div key={idx} className="relative p-6 group">
                                <div className="absolute inset-0 bg-white rounded-sm shadow-sm transform transition-transform group-hover:-translate-y-2 group-hover:shadow-md"></div>
                                <div className="relative z-10">
                                    <div className="text-5xl font-bold text-purple-600 mb-3">{stat.val}</div>
                                    <div className="text-sm text-slate-500 uppercase tracking-widest">{stat.label}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services Grid (Hover Cards) */}
            <section id="services" className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-light text-slate-900 mb-4">ML Capabilities</h2>
                        <p className="text-lg text-slate-600">
                            From computer vision to predictive maintenance.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {services.map((service, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.1 }}
                                className="group relative h-80 overflow-hidden rounded-sm bg-slate-900 cursor-pointer"
                            >
                                <div className="absolute inset-0 bg-cover bg-center opacity-60 transition-transform duration-700 group-hover:scale-110 group-hover:opacity-40" style={{ backgroundImage: `url(${service.image})` }}></div>
                                <div className="absolute inset-0 bg-gradient-to-t from-indigo-900 via-transparent to-transparent opacity-90"></div>

                                <div className="absolute inset-0 p-8 flex flex-col justify-end z-10">
                                    <div className="transform transition-transform duration-500 group-hover:-translate-y-4">
                                        <service.icon className="text-4xl text-purple-400 mb-4" />
                                        <h3 className="text-2xl font-light text-white mb-2">{service.title}</h3>
                                        <div className="h-0 overflow-hidden group-hover:h-auto transition-all duration-500">
                                            <p className="text-purple-100 text-sm leading-relaxed mt-2 opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-100">{service.desc}</p>
                                        </div>
                                    </div>
                                    <div className="w-12 h-1 bg-purple-500 mt-4 transform origin-left transition-all duration-500 group-hover:w-full"></div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Tech Stack */}
            <section className="py-20 bg-slate-50 border-y border-slate-200">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <p className="text-center text-sm font-bold text-slate-400 uppercase tracking-widest mb-10">Our ML Toolchain</p>
                    <div className="flex flex-wrap justify-center gap-8 md:gap-16 opacity-70 grayscale hover:grayscale-0 transition-all duration-500">
                        {techStack.map((tech, idx) => (
                            <div key={idx} className="flex flex-col items-center gap-2 group cursor-default">
                                <tech.icon className="text-4xl text-slate-600 group-hover:text-purple-600 transition-colors" />
                                <span className="text-sm font-medium text-slate-500 group-hover:text-slate-800">{tech.name}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Timeline (Implementation Process) */}
            <section className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-20">
                        <h2 className="text-3xl md:text-4xl font-light text-slate-900 mb-4">MLOps Lifecycle</h2>
                        <p className="text-slate-600">A rigorous path from raw data to deployed intelligence.</p>
                    </div>

                    <div className="relative">
                        {/* Base Line (Grey) */}
                        <div className="hidden md:block absolute top-[40px] left-0 w-full h-0.5 bg-slate-200 -z-0"></div>

                        {/* Steps Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-6 gap-y-12 gap-x-4 relative">
                            {flowSteps.map((step, idx) => (
                                <div key={idx} className="relative flex flex-col items-center group">
                                    {/* Circle Icon */}
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.5 }}
                                        whileInView={{ opacity: 1, scale: 1 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: idx * 0.1, type: "spring", stiffness: 100 }}
                                        className="relative z-20 mb-6"
                                    >
                                        <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center text-white text-2xl shadow-xl group-hover:scale-110 transition-transform duration-300 ring-4 ring-white`}>
                                            <step.icon />
                                        </div>
                                    </motion.div>

                                    {/* Title */}
                                    <h3 className="text-lg font-bold text-slate-800 text-center">{step.title}</h3>

                                    {/* Mobile Connector */}
                                    {idx < flowSteps.length - 1 && (
                                        <div className="md:hidden absolute -bottom-8 left-1/2 w-0.5 h-8 bg-slate-200"></div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Why Choose Us */}
            <section className="py-24 bg-indigo-900 text-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="grid md:grid-cols-2 gap-16 items-center">
                        <div>
                            <p className="text-purple-400 font-bold tracking-widest uppercase mb-4">Why Tech-Inovant ML</p>
                            <h2 className="text-4xl md:text-5xl font-light mb-8 leading-tight">Precision <br />Engineering</h2>
                            <p className="text-indigo-200 text-lg leading-relaxed mb-8">
                                We rely on mathematical proof, not hype. Every model we deliver is rigorously tested for bias, variance, and robustness.
                            </p>
                            <a href="#inquiry" className="inline-flex items-center text-purple-400 font-medium hover:text-white transition-colors">
                                Schedule a Demo <FaChevronRight className="ml-2 text-xs" />
                            </a>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-6">
                            {whyChooseUs.map((item, idx) => (
                                <div key={idx} className="bg-indigo-800/50 p-6 rounded-sm border border-indigo-700 hover:border-purple-500/50 transition-colors">
                                    <FaCheckCircle className="text-purple-500 text-xl mb-3" />
                                    <h3 className="text-lg font-medium mb-2">{item.title}</h3>
                                    <p className="text-indigo-200 text-sm leading-relaxed">{item.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ & Inquiry Form */}
            <section id="inquiry" className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="grid lg:grid-cols-2 gap-16">

                        {/* FAQs */}
                        <div>
                            <h2 className="text-3xl font-light text-slate-900 mb-8">ML FAQs</h2>
                            <div className="space-y-4">
                                {faqs.map((faq, idx) => (
                                    <div key={idx} className="border-b border-slate-200">
                                        <button
                                            onClick={() => toggleFaq(idx)}
                                            className="w-full py-6 text-left flex justify-between items-center group"
                                        >
                                            <span className="text-lg text-slate-800 font-light group-hover:text-purple-600 transition-colors">{faq.q}</span>
                                            {openFaq === idx ? <FaChevronUp className="text-purple-500" /> : <FaChevronDown className="text-slate-300" />}
                                        </button>
                                        <motion.div
                                            initial={false}
                                            animate={{ height: openFaq === idx ? 'auto' : 0, opacity: openFaq === idx ? 1 : 0 }}
                                            className="overflow-hidden"
                                        >
                                            <p className="pb-6 text-slate-500 leading-relaxed">{faq.a}</p>
                                        </motion.div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Inquiry Form */}
                        <div className="bg-slate-50 p-10 rounded-sm border border-slate-200">
                            <h2 className="text-3xl font-light text-slate-900 mb-2">Start Predicting</h2>
                            <p className="text-slate-500 mb-8">Discuss your data strategy with our scientists.</p>

                            <form className="space-y-6">
                                <div className="grid grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Name</label>
                                        <input type="text" className="w-full bg-white px-4 py-3 border-b-2 border-slate-200 focus:border-purple-500 outline-none transition-colors" placeholder="John Doe" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Company</label>
                                        <input type="text" className="w-full bg-white px-4 py-3 border-b-2 border-slate-200 focus:border-purple-500 outline-none transition-colors" placeholder="Acme Inc." />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Email</label>
                                    <input type="email" className="w-full bg-white px-4 py-3 border-b-2 border-slate-200 focus:border-purple-500 outline-none transition-colors" placeholder="john@acme.com" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Data Type</label>
                                    <select className="w-full bg-white px-4 py-3 border-b-2 border-slate-200 focus:border-purple-500 outline-none transition-colors text-slate-700">
                                        <option>Structured (SQL/Excel)</option>
                                        <option>Images / Video</option>
                                        <option>Text / NLP</option>
                                        <option>Time-Series / Sensor</option>
                                    </select>
                                </div>

                                <button type="submit" className="w-full bg-purple-600 text-white font-bold py-4 rounded-sm hover:bg-purple-700 transition-all shadow-lg mt-4 uppercase tracking-widest text-sm">
                                    Analyze Feasibility
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default MachineLearningPage;
